import axios from 'axios';

import { baseUrl } from 'config/baseUrl';
import { PATHS } from 'config/constants';
import { handleErrorPayload } from 'utils/handleErrorPayload';

import { getproductadaptour } from 'adaptors/campagin/product';
import { getCampaignsAdaptors } from 'adaptors/campaign/list.adaptor';
import { getCampaignReportByStatusAdaptor } from 'adaptors/dashboard/status-report.adaptor';
import {
  activateCampaign,
  createAndActivateCampaign,
  createCampaign,
  deactivateCampaign,
  deleteCampaign,
  getCampaign,
  getCampaignProducts,
  getCampaigns,
  updateAndActivateCampaign,
  updateCampaign,
} from 'rest/dist/gen/api/campaigns/campaigns';
import {
  getTopFiveCampaigns,
  orderReport,
  releaseNote,
} from 'rest/dist/gen/api/dashboard/dashboard';
import { getPlans as getPlansApi, upgradePlan } from 'rest/dist/gen/api/pricing/pricing';
import {
  getB2bSignals as getB2bSignalsApi,
  getProductTags,
  getProductTypes,
  getShopInfo,
  getShopStorage,
  getThemeEnabled,
  getVariantMetafields as getVariantMetafieldsApi,
  getVendors,
  hasThemeAccess,
  setcartEmbedSettings,
  setComingSoonClicked,
  setShopStorage,
} from 'rest/dist/gen/api/shops/shops';
// Type-only: a runtime `import { ShopStorageDto } from 'rest/dist/gen/models'`
// pulled the whole 238-module CJS barrel (~1.1MB of generated code) into the
// main bundle for a value that doesn't exist at runtime. JSDoc import() keeps
// the type without any bundle cost.
/** @typedef {import('rest/dist/gen/models/shopStorageDto').ShopStorageDto} ShopStorageDto */

import { saveCampaignAdaptor } from 'adaptors/campaign/save.adaptor';

import { singleCampaignFormAdaptor } from 'adaptors/campaign/get.adaptor';
import { extractGiftOverlaps } from 'utils/giftOverlapNotice';
import { createPlansAdaptor } from 'adaptors/plans/plans-adaptor';
import { getSettingDataAdaptor, setSettingDataAdaptor } from 'adaptors/setting/setting-adaptors';
import { getSettings, setSettings } from 'rest/dist/gen/api/settings/settings';
import { sendSupportMail } from 'rest/dist/gen/api/supports/supports';
import { getChatwootIdentity as getChatwootIdentityApi } from 'rest/dist/gen/api/chatwoot/chatwoot';
import { fetchInstance } from 'rest/dist/utils/fetchInstance';

class Api {
  constructor(baseUrl) {
    this.axios = axios.create();
    this.axios.defaults.baseURL = baseUrl;
    this.axios.defaults.timeout = 90000;
    this.axios.defaults.headers['Content-Type'] = 'application/json';
  }

  setAppBridge(host, shopDomain, loggedInAsUser = false) {
    this.loggedInAsUser = loggedInAsUser;

    // 2026-07 migration: this used to createApp()/initializeWebVitals() a v3
    // App Bridge instance when !loggedInAsUser — but BOTH call sites of
    // setAppBridge (App.js, Login.js) always pass loggedInAsUser=true, so
    // this branch never actually ran. Removed along with v3 entirely; the
    // app is v4-only now (window.shopify, see _getSessionTokenShared below).

    this.axios.interceptors.request.use((config) => {
      if (loggedInAsUser) {
        config.headers['Authorization'] = this._getToken();
        config.headers['admin-login'] = true;
        config.headers['shop-domain'] = this._getShopDomain();
        return config;
      }

      return this._getSessionTokenShared().then((token) => {
        config.headers['shopify-authorization'] = `Bearer ${token}`;
        config.headers['Authorization'] = `Bearer ${token}`;
        config.headers['shop-domain'] = shopDomain;
        return config;
      });
    });
  }

  // getSessionToken() does a full postMessage round-trip to the parent admin
  // frame every time it's called — with no sharing, a page that fires several
  // requests on mount (e.g. Pricing: plans, plan cards, comparison table)
  // triggers that many separate round-trips in parallel instead of one. Share
  // one in-flight/recent token across requests fired close together; a token
  // is valid for ~1 minute per Shopify, so a short reuse window is safe.
  _getSessionTokenShared() {
    const now = Date.now();
    if (this._sessionTokenPromise && now - (this._sessionTokenFetchedAt || 0) < 5000) {
      return this._sessionTokenPromise;
    }
    this._sessionTokenFetchedAt = now;
    // 2026-07 migration: v3 fallback (getSessionToken(this.app)) removed —
    // this.app was only ever set in the dead createApp() branch above (both
    // callers of setAppBridge pass loggedInAsUser=true), so this path is
    // itself unreachable today (see setAppBridge above); kept working
    // correctly on window.shopify.idToken() regardless, matching
    // fetchInstance.ts's now-identical v4-only implementation.
    const source = window.shopify.idToken();
    this._sessionTokenPromise = source.catch((err) => {
      this._sessionTokenPromise = null;
      throw err;
    });
    return this._sessionTokenPromise;
  }

  _getToken() {
    let token;
    if ('sessionStorage' in window) token = sessionStorage.getItem('loggedInAsUserToken');
    if (!token) return '';
    // return token;
    return `Bearer ${token}`;
  }

  _getShopDomain() {
    let shopDomain;
    if ('sessionStorage' in window) shopDomain = sessionStorage.getItem('loggedInAsUserShopDomain');
    if (!shopDomain) return '';
    return shopDomain;
  }

  _handleErrors(err) {
    if (err?.response?.status === 401) {
      if (this.loggedInAsUser) {
        window.open(window.location.origin + PATHS.LOGIN + '?message=INVALID_TOKEN', '_self');
      } else {
        // What to do in shopify iframe
      }
    }

    const errorMessage = handleErrorPayload(err);
    return Promise.reject(errorMessage);
  }

  async get(...params) {
    return this.axios.get(...params).catch((err) => this._handleErrors(err));
  }

  async post(...params) {
    return this.axios.post(...params).catch((err) => this._handleErrors(err));
  }

  async patch(...params) {
    return this.axios.patch(...params).catch((err) => this._handleErrors(err));
  }

  async put(...params) {
    return this.axios.put(...params).catch((err) => this._handleErrors(err));
  }

  async delete(...params) {
    return this.axios.delete(...params).catch((err) => this._handleErrors(err));
  }

  // Authentication
  loginAsUser(data) {
    return this.post(`/support/login/panel/`, data);
  }

  // Plans

  // App.js fires getPlansAction the moment the shopInfo gate clears, and the
  // Pricing page fires its own getPlans() again on mount ~300ms later —
  // measured on staging as two full duplicate /api/pricing/plans round trips,
  // with the page waiting on the SLOWER second one. Share one in-flight/recent
  // fetch the same way getShopStorage()/_getSessionTokenShared() do; explicit
  // params (e.g. discount lookups) bypass the cache.
  async getPlans(params) {
    if (params) return this._fetchPlans(params);
    const now = Date.now();
    if (this._plansPromise && now - (this._plansFetchedAt || 0) < 5000) {
      return this._plansPromise;
    }
    this._plansFetchedAt = now;
    this._plansPromise = this._fetchPlans(params).catch((err) => {
      this._plansPromise = null;
      throw err;
    });
    return this._plansPromise;
  }

  async _fetchPlans(params) {
    const res = await getPlansApi(params);

    const raw = res?.data ?? res ?? [];

    return {
      data: {
        success: true,
        data: raw,
        message: 'ok',
      },
    };
  }

  async getPlansAdapted(params) {
    const result = await getPlansApi(params);
    return createPlansAdaptor(result);
  }

  async upgradePlan(params) {
    /** @type {import('rest/dist/gen/models').UpgradePlanDto} */
    var mymodel = {
      planId: params.planId,
      period: params.selectedperiod === 'annually' ? 'annual' : 'monthly',
      discountCode: params.discountCode,
    };
    var result = await upgradePlan(mymodel);
    return result;
    // return this.get(`/billing/updatePlan${params}`);
  }

  async orderRevenueReport(params) {
    const data = await orderReport(params);
    const mapedData = data.data.map((x) => ({ date: x._id, revenue: x.revenue, discount: x.discount ?? 0 }));

    return { data: { data: mapedData, total: data.totalRevenue, success: true, message: 'ok' } };
    // return this.get(`api/auction/orderRevenueReport${params}`);
  }

  async orderCountReport(params) {
    const data = await orderReport(params);
    const mapedData = data.data.map((x) => ({ date: x._id, count: x.count }));

    return { data: { data: mapedData, total: data.totalCount, success: true, message: 'ok' } };
    // return this.get(`api/auction/orderCountReport${params}`);
  }

  cancelPlan() {
    return this.get(`/app-subscription/cancel/`);
  }
  getProductList() {
    return this.get(`/api/auction/productList/`);
  }
  getCollectionList() {
    return this.get(`/api/auction/collectionList/`);
  }

  applyDiscount(discountCode) {
    return this.get(`/discounts/${discountCode}/`);
  }

  // Shop Info
  getShopInfo() {
    return this.get('/api/shop/shopInfo');
  }
  // Smart B2B detection (product costs + customer segments) for the dashboard
  // Wholesale & B2B card. Goes through the Orval-generated client; returns
  // { data: { hasCostData, hasSegments } }.
  async getB2bSignals() {
    const res = await getB2bSignalsApi();
    return { data: res?.data ?? { hasCostData: false, hasSegments: false } };
  }
  // Sidekick support (see src/sidekick and Prime-Backend src/api/sidekick).
  // Maps the Shopify discount id from an edit intent to the campaign(s) behind it.
  resolveSidekickDiscount(discountId) {
    return this.get(`/api/sidekick/resolve-discount/${encodeURIComponent(discountId)}`);
  }
  // Opportunity scanner (weekly findings). view=page lists every open finding for the app screen.
  getSidekickOpportunities() {
    return this.get('/api/sidekick/opportunities?view=page');
  }
  getSidekickOpportunity(id) {
    return this.get(`/api/sidekick/opportunities/${encodeURIComponent(id)}`);
  }
  updateSidekickOpportunity(id, body) {
    return this.patch(`/api/sidekick/opportunities/${encodeURIComponent(id)}`, body);
  }
  // Raw server campaigns (status, startAt, endAt, ranAt) for the campaign calendar.
  async getCampaignCalendar() {
    const campaigns = await getCampaigns({ pageSize: 200 });
    return { data: campaigns?.data ?? [] };
  }
  // Checkout simulator: prices a hypothetical cart with the real discount function. Writes nothing.
  simulateSidekickCart(body) {
    return this.post('/api/sidekick/simulate', body);
  }
  // Product / collection name -> ids for the Sidekick page tool set_products.
  resolveSidekickResource(kind, q) {
    return this.get(`/api/sidekick/resolve?kind=${encodeURIComponent(kind)}&q=${encodeURIComponent(q)}`);
  }

  getShopPlanInfo() {
    return this.get('/api/shop/shopPlanInfo');
  }
  getPermission() {
    return this.get('/api/shop/getPermission/');
  }

  getIsInstalledShop(shop) {
    return this.get('/api/shop/getIsInstalledShop?shop=' + shop);
  }

  async createAuction(data) {
    const campaign = saveCampaignAdaptor(data);

    if (data.id) {
      if (data.isActive) {
        await updateAndActivateCampaign(data.id, campaign);
      } else {
        if ([13, 5].includes(data.originalStatus)) {
          await deactivateCampaign(data.id);
        }
        await updateCampaign(data.id, campaign);
      }
    } else {
      if (data.isActive) {
        const res = await createAndActivateCampaign(campaign);
        data.id = res?.data?.id;
      } else {
        const res = await createCampaign(campaign);
        data.id = res?.data?.id;
      }
    }

    const fresh = await getCampaign(data.id);
    const adapted = singleCampaignFormAdaptor(fresh.data);

    return {
      data: {
        data: adapted,
        showLimitationWarning: false,
        success: true,
        message: 'ok',
      },
    };
  }

  async getAuction(auctionId) {
    const data = await getCampaign(auctionId);
    const res = singleCampaignFormAdaptor(data.data);
    return { data: res };
    // return this.get(`/api/auction/getAuction?auctionId=${auctionId}`);
  }
  async getAuctionProducts(auctionId) {
    return getproductadaptour(await getCampaignProducts(auctionId, { page: 1, pageSize: 100 }));
    // return this.get(`/api/auction/getAuctionWithProducts?auctionId=${auctionId}`);
  }
  async getAuctions() {
    const campaigns = await getCampaigns({ pageSize: 200 });
    const adaptedData = getCampaignsAdaptors(campaigns.data);
    return { data: adaptedData };
    // return this.get(`/api/auction/getAuctions`);
  }

  // ── Bulk discount codes ────────────────────────────────────────────────────
  // Use the shared fetchInstance (same auth/session-token flow as the rest of the
  // generated client) — the legacy this.axios path isn't authenticated in the
  // embedded app. fetchInstance returns the parsed JSON body directly.
  async getCampaignCodes(campaignId, { search, page, pageSize } = {}) {
    const params = {};
    if (search) params.search = search;
    if (page) params.page = page;
    if (pageSize) params.pageSize = pageSize;
    return fetchInstance({
      url: `/api/campaigns/${campaignId}/codes`,
      method: 'get',
      ...(Object.keys(params).length ? { params } : {}),
    });
  }

  async getDiscountCodeDetail(codeId) {
    return fetchInstance({ url: `/api/campaigns/codes/${codeId}`, method: 'get' });
  }

  // Returns a Blob for the chosen export format ('csv' | 'xlsx').
  async exportCampaignCodes(campaignId, format = 'csv') {
    return fetchInstance({
      url: `/api/campaigns/${campaignId}/codes/export`,
      method: 'get',
      params: { format },
      responseType: 'blob',
    });
  }
  async getTagList() {
    const tags = await getProductTags();
    return { data: { ...tags, success: true } };
    // return this.get(`/api/common/resource/tagList`);
  }
  async getVendorList() {
    const vendors = await getVendors();
    return { data: { ...vendors, success: true } };
    // return this.get(`/api/common/resource/vendorList`);
  }
  async getProductTypeList() {
    const productTypes = await getProductTypes();
    return { data: { ...productTypes, success: true } };
    // return this.get(`/api/common/resource/productTypeList`);
  }
  // Per-value ACTIVE product counts (Merchant Data Platform read model).
  // Called through fetchInstance directly rather than the Orval client
  // because these three routes (`/shops/{productTags,vendors,productTypes}/counts`)
  // are new and the generated client has not been regenerated against them yet
  // — same escape hatch `exportCampaignCodes` above uses. `counts: null` means
  // the shop can't be served locally yet; callers must treat that as "loading",
  // never as an empty list — see valueCounts.js's own comment for why.
  async getTagCounts() {
    return fetchInstance({ url: '/shops/productTags/counts', method: 'get' });
  }
  async getVendorCounts() {
    return fetchInstance({ url: '/shops/vendors/counts', method: 'get' });
  }
  async getProductTypeCounts() {
    return fetchInstance({ url: '/shops/productTypes/counts', method: 'get' });
  }
  // Variant (PRODUCTVARIANT) metafield definitions for the campaign rule builder.
  // Goes through the generated Orval client (fetchInstance handles the session
  // token + 403 re-auth), same as getTagList. Backend returns { data: [...] }.
  async getVariantMetafields() {
    const result = await getVariantMetafieldsApi();
    return { data: { ...result, success: true } };
  }
  async disableAuctions(data) {
    var lenofids = data.auctionIds.length;
    const failed = [];
    let firstError = null;
    for (let index = 0; index < lenofids; index++) {
      const element = data.auctionIds[index];
      // QA T4: one rejected call (e.g. a campaign the backend refuses to touch mid-activation)
      // used to abort the loop and leave every later campaign live. Try them all.
      try {
        await deactivateCampaign(element);
      } catch (e) {
        failed.push(element);
        firstError = firstError || e;
      }
    }
    if (failed.length && failed.length === lenofids) throw firstError;
    return { data: { success: true, failed } };
    // return this.post(`/api/auction/disableAuction/`, data);
  }
  async enableAuctions(data) {
    var lenofids = data.auctionIds.length;
    // CFL-5: free gifts that now share a gift item with another live gift campaign (warning only)
    const giftOverlaps = [];
    for (let index = 0; index < lenofids; index++) {
      const element = data.auctionIds[index];
      const activated = await activateCampaign(element);
      giftOverlaps.push(...extractGiftOverlaps(activated));
    }
    return { data: { success: true, giftOverlaps } };
    // return this.post(`/api/auction/enableAuction/`, data);
  }
  async updateAndActivateCampaignForceConflict(data) {
    const campaign = saveCampaignAdaptor({
      ...data,
      forceConflictStart: true,
      isActive: true,
    });

    if (!data?.id) {
      const res = await createAndActivateCampaign(campaign);
      return { data: { success: true, id: res?.data?.id } };
    }

    await updateAndActivateCampaign(data.id, campaign);
    return { data: { success: true } };
  }
  archiveAuctions(data) {
    return this.post(`/api/auction/archiveAuctions/`, data);
  }
  unArchiveAuctions(data) {
    return this.post(`/api/auction/unarchiveAuctions/`, data);
  }
  async deleteAuctions(data) {
    var lenofids = data.auctionIds.length;
    for (let index = 0; index < lenofids; index++) {
      const element = data.auctionIds[index];
      var result = await deleteCampaign(element);
    }
    return { data: { success: true } };
  }
  getEstimateProducts(data) {
    return this.post(`/api/auction/estimateProducts/`, data);
  }
  getEstimateProductsResult(params) {
    return this.get(`/api/auction/estimateProductsResult${params}`);
  }
  getNotifications(params) {
    return this.get(`/api/common/getNotifications${params}`);
  }
  setNotificationClose(data) {
    return this.post(`/api/common/setNotificationClose/`, data);
  }
  setNotificationView(data) {
    return this.post(`/api/common/setNotificationView/`, data);
  }
  getOption(params) {
    return this.get(`/api/common/getOption${params}`);
  }
  async getPlansWithDiscount(params) {
    const model = { discountCode: params };

    try {
      const res = await getPlansApi(model);
      const raw = res?.data ?? res ?? [];

      return {
        data: {
          success: true,
          data: raw,
          message: 'ok',
        },
      };
    } catch (error) {
      return {
        data: {
          success: false,
          message_key: 'msg_discount_code_not_valid',
          message: 'Discount Code is not Valid',
        },
      };
    }
  }
  async getShopSetting() {
    const settingsData = getSettingDataAdaptor(await getSettings());
    return settingsData;
  }
  async saveShopSetting(data) {
    const saveSettingData = await setSettings(setSettingDataAdaptor(data));
    return saveSettingData;
  }
  async getChatwootIdentity() {
    const res = await getChatwootIdentityApi();
    return res?.data;
  }
  wasItHelpful(data) {
    return this.post(`/api/common/helpfulPage/`, data);
  }
  sendSupportEmail(data) {
    if (data.subject == '') {
      data.subject = 'Campaign creation';
    }
    return sendSupportMail(data);
    // return this.post(`/api/common/sendSupportEmail/`, data);
  }
  async updateCampaignEndDate(data) {
    // const campaign = saveCampaignAdaptor(data);
    await deactivateCampaign(data.auctionId);
    return { data: { success: true } };

    // return this.post(`/api/auction/updateCampaignEndDate/`, data);
  }

  async getCampaignReportByStatus() {
    const data = await getCampaignReportByStatusAdaptor();
    return { data };

    // return this.get(`api/auction/dashboardStatus`);
    // return campaignStatusDetail();
  }
  async getTopFiveCampaign() {
    const campaigns = await getTopFiveCampaigns();
    const adaptedData = getCampaignsAdaptors(campaigns.data);
    return { data: adaptedData };
    // return this.get(`api/auction/getTopFiveCampaign`);
  }
  getFaqList() {
    return this.get(`/api/common/getFAQList`);
  }
  campaignForceStop(id) {
    return this.get(`/api/auction/forceStop?auctionId=${id}`);
  }

  // Every useShopStorageFlag() instance calls this with no params and gets back
  // the SAME full storage list (each just reads its own key out of it locally)
  // — a dashboard mount that renders several self-gating banners (Onboarding,
  // GiftBanner, UsageLimitWarningBanner, PostUpgradeInfoBanner...) fires that
  // many IDENTICAL requests in parallel instead of one, adding real queued
  // network latency to whatever gates on all of them being ready. Share one
  // in-flight/recent fetch the same way _getSessionTokenShared() does.
  async getShopStorage(params) {
    if (params) return this._fetchShopStorage(params);
    const now = Date.now();
    if (this._shopStoragePromise && now - (this._shopStorageFetchedAt || 0) < 3000) {
      return this._shopStoragePromise;
    }
    this._shopStorageFetchedAt = now;
    this._shopStoragePromise = this._fetchShopStorage(params).catch((err) => {
      this._shopStoragePromise = null;
      throw err;
    });
    return this._shopStoragePromise;
  }

  async _fetchShopStorage(params) {
    const res = await getShopStorage(params);
    const v = res?.data ?? res;
    if (v && typeof v === 'object' && 'data' in v) return { data: v };
    return { data: { data: v } };
  }

  async setShopStorage(items) {
    return setShopStorage(items);
  }
  onboarding(data) {
    /** @type {ShopStorageDto[]} */
    var dtoshopstorage = [];
    if (
      data.onboardingFirstCampaign == true
      // data == 'onboardingFirstCampaign'
    ) {
      /** @type {ShopStorageDto} */
      var mymodel = { key: 'onboardingFirstCampaign', value: true };
      dtoshopstorage.push(mymodel);
    }
    if (
      data.onboardingActivateCampaign == true
      // data == 'onboardingActivateCampaign'
    ) {
      /** @type {ShopStorageDto} */
      var mymodel = { key: 'onboardingActivateCampaign', value: true };
      dtoshopstorage.push(mymodel);
    }
    if (
      data.onboardingNeedUpgradeCampaign !== undefined
      // data == 'onboardingNeedUpgradeCampaign'
    ) {
      /** @type {ShopStorageDto} */
      var mymodel = {
        key: 'onboardingNeedUpgradeCampaign',
        value: data.onboardingNeedUpgradeCampaign,
      };
      dtoshopstorage.push(mymodel);
    }
    if (
      // data == 'onboardingThemeExtension'
      data.onboardingThemeExtension == true
    ) {
      /** @type {ShopStorageDto} */
      var mymodel = { key: 'onboardingThemeExtension', value: true };
      dtoshopstorage.push(mymodel);
    }
    if (
      data.onboardingThemeExtension2 == true
      // data == 'onboardingThemeExtension2'
    ) {
      /** @type {ShopStorageDto} */
      var mymodel = { key: 'onboardingThemeExtension2', value: true };
      dtoshopstorage.push(mymodel);
    }
    if (
      data.onboardingDismiss == true
      // data == 'onboardingDismiss'
    ) {
      /** @type {ShopStorageDto} */
      var mymodel = { key: 'onboardingDismiss', value: true };
      dtoshopstorage.push(mymodel);
    }

    if (
      data.onboardingFinalEnableCheck == true
      // data == 'onboardingDismiss'
    ) {
      /** @type {ShopStorageDto} */
      var mymodel = { key: 'onboardingFinalEnableCheck', value: true };
      dtoshopstorage.push(mymodel);
    }
    if (data.onboardingVideo == true) {
      /** @type {ShopStorageDto} */
      var mymodel = { key: 'onboardingVideo', value: true };
      dtoshopstorage.push(mymodel);
    }
    return setShopStorage(dtoshopstorage);
    // return this.post(`/api/shop/onboarding/`, data);
  }
  async getCartEmbedSetting() {
    const data = await getShopInfo();
    return { data };
  }
  async setCartEmbedSetting(data) {
    await setcartEmbedSettings(data);

    // return { data: res };
    return { data: { success: true } };
  }

  async getReleaseNotes() {
    const res = await releaseNote();
    return { data: res };
  }

  _toBool(v) {
    if (typeof v === 'boolean') return v;
    if (typeof v === 'string') {
      const s = v.trim().toLowerCase();
      if (s === 'true') return true;
      if (s === 'false') return false;
      if (s === '1') return true;
      if (s === '0') return false;
    }
    return Boolean(v);
  }

  _unwrapData(res) {
    const v = res?.data ?? res;

    if (v && typeof v === 'object' && 'data' in v) return v.data;
    return v;
  }

  // Same shared-promise pattern as _getSessionTokenShared() above — a SEPARATE
  // cache from adaptors/shop/shop-info.js's getThemeExtentionActivation/
  // hastThemeScope (which wrap these same 2 endpoints for the OTHER call path,
  // useThemeExtensionActivation()). Both this Api class's methods (used by
  // useAppEmbedStatus, CampaignFinalEnableCheckModal, dashboardV2/Onboarding)
  // and that hook's 13+ consumers were independently hitting
  // /api/shops/themeEnabled and /api/shops/has_theme_access with zero sharing
  // between the two paths — measured on a real trace: 6 separate themeEnabled
  // calls and 3 has_theme_access calls in one dashboard load, several taking
  // 500ms-1s each. Caching the OTHER path alone didn't fix this one.
  //
  // Window is deliberately short (unlike the session token's 5s) — long enough
  // to dedupe several components mounting in the same render burst (a few ms
  // apart), but well under useAppEmbedStatus's explicit 1500ms-spaced retry
  // loop (retries after activating the embed, when the backend can briefly
  // return a stale/unresolved answer) and its 3000ms focus-recheck guard. A
  // longer window would have silently fed that retry loop its own stale result.
  _THEME_STATUS_REUSE_WINDOW_MS = 300;

  _themeAccessShared() {
    const now = Date.now();
    if (this._themeAccessPromise && now - (this._themeAccessFetchedAt || 0) < this._THEME_STATUS_REUSE_WINDOW_MS) {
      return this._themeAccessPromise;
    }
    this._themeAccessFetchedAt = now;
    this._themeAccessPromise = hasThemeAccess().catch((err) => {
      this._themeAccessPromise = null;
      throw err;
    });
    return this._themeAccessPromise;
  }

  _appEmbedStatusShared() {
    const now = Date.now();
    if (this._appEmbedStatusPromise && now - (this._appEmbedStatusFetchedAt || 0) < this._THEME_STATUS_REUSE_WINDOW_MS) {
      return this._appEmbedStatusPromise;
    }
    this._appEmbedStatusFetchedAt = now;
    this._appEmbedStatusPromise = getThemeEnabled().catch((err) => {
      this._appEmbedStatusPromise = null;
      throw err;
    });
    return this._appEmbedStatusPromise;
  }

  async getThemeAccess() {
    const res = await this._themeAccessShared(); // GET /api/shops/has_theme_access
    const raw = this._unwrapData(res);
    return this._toBool(raw);
  }

  async getAppEmbedStatus() {
    // It returns "themeEnabled" state (Shopify), which come as "false"/"true" STRING
    const res = await this._appEmbedStatusShared(); // GET /api/shops/themeEnabled
    const raw = this._unwrapData(res);
    const themeEnabled = this._toBool(raw);

    // A DEFINITIVE answer is only 'true'/'false' (or a real boolean). Right after a
    // reinstall the new offline token can lag, so the backend's theme query returns
    // '' or 'null' (no theme data) — which _toBool silently coerces to a wrong
    // boolean. Flag that so the caller can retry instead of locking onto it.
    const resolved =
      typeof raw === 'boolean' ||
      (typeof raw === 'string' && ['true', 'false', '1', '0'].includes(raw.trim().toLowerCase()));

    // Shopify returns a value that needs to be inverted for our "embedEnabled" meaning.
    // When themeEnabled is false => embed is enabled.
    return { themeEnabled, resolved };
  }

  async setComingSoonClicked(dto) {
    try {
      return await setComingSoonClicked(dto);
    } catch (e) {
      return null;
    }
  }
}

export const api = new Api(baseUrl);
